// SPDX-License-Identifier: AGPL-3.0-or-later

import Window from '@app/features/window/state/Window';
import {reaction} from 'mobx';

/** One non-overlapping refresh loop; hidden windows have no scheduled poll. */
export class VisiblePoll {
	private dispose: (() => void) | null = null;
	private timer: ReturnType<typeof setTimeout> | null = null;
	private pending: Promise<unknown> | null = null;
	private lastCompletedAt: number | null = null;
	private firstRefreshAt: number | null = null;
	private refreshOnResume = false;

	constructor(
		private readonly intervalMs: number,
		private readonly refresh: () => unknown | Promise<unknown>,
		private readonly alwaysRefreshOnResume = false,
		/** Leave startup loading to the store when it already has a separate initial request. */
		private readonly initialDelayMs = 0,
	) {}

	start(): void {
		if (this.dispose) return;
		this.firstRefreshAt ??= Date.now() + this.initialDelayMs;
		this.dispose = reaction(
			() => Window.visible,
			(visible, previous) => {
				this.clearTimer();
				if (!visible) return;
				this.refreshOnResume ||= previous === false && this.alwaysRefreshOnResume;
				this.schedule();
			},
			{fireImmediately: true},
		);
	}

	stop(): void {
		this.dispose?.();
		this.dispose = null;
		this.clearTimer();
		this.refreshOnResume = false;
	}

	private clearTimer(): void {
		if (this.timer !== null) clearTimeout(this.timer);
		this.timer = null;
	}

	private schedule(): void {
		if (!Window.visible || this.pending) return;
		this.clearTimer();
		const nextAt =
			this.lastCompletedAt === null ? (this.firstRefreshAt ?? Date.now()) : this.lastCompletedAt + this.intervalMs;
		const delay = this.refreshOnResume ? 0 : Math.max(0, nextAt - Date.now());
		this.timer = setTimeout(() => {
			this.timer = null;
			if (!this.dispose || !Window.visible) return;
			this.refreshOnResume = false;
			this.pending = Promise.resolve()
				.then(() => this.refresh())
				.catch(() => {
					// Stores own their error state; retry at the normal cadence.
				})
				.finally(() => {
					this.lastCompletedAt = Date.now();
					this.pending = null;
					if (this.dispose) this.schedule();
				});
		}, delay);
	}
}
